<template>
  <multiselect ref="multiselect"
               v-model="selectedOptions"
               :options="optionsList"
               :multiple="true"
               :clear-on-select="false"
               :close-on-select="false"
               :preserve-search="true"
               :prevent-autofocus="true"
               :show-labels="false"
               :show-no-results="false"
               :show-no-options="false"
               :allow-empty="true"
               :placeholder="$t('settings.generic.select-items')"
               label="fullName" track-by="key"
               @close="onMultiselectClose"
               class="tracking-multiselect fit-content attributes-select">
    <template #option="props">
      <div class="tracking-multiselect-with-checkbox position-attribute-option">
        <div class="position-attribute-option-main">
          <input type="checkbox" :checked="isOptionChecked(props.option.key)" @focus.prevent :key="props.option.key" />
          <span class="option__title">{{ $t(props.option.descriptionKey) }}</span>
        </div>
        <div v-if="supportsThresholds(props.option)"
             class="position-attribute-thresholds"
             @click.stop
             @mousedown.stop
             @keydown.stop>
          <label class="position-attribute-threshold-field">
            <span>{{ $t('reports.page.positions-data-chart.min-value') }}</span>
            <input type="number"
                   step="any"
                   inputmode="decimal"
                   class="position-attribute-threshold-input"
                   :value="getThresholdValue(props.option.key, 'min')"
                   @mousedown.stop="onThresholdMouseDown(props.option)"
                   @focus="onThresholdFocus(props.option)"
                   @blur="onThresholdBlur"
                   @input="onThresholdInput(props.option.key, 'min', $event.target.value)" />
          </label>
          <label class="position-attribute-threshold-field">
            <span>{{ $t('reports.page.positions-data-chart.max-value') }}</span>
            <input type="number"
                   step="any"
                   inputmode="decimal"
                   class="position-attribute-threshold-input"
                   :value="getThresholdValue(props.option.key, 'max')"
                   @mousedown.stop="onThresholdMouseDown(props.option)"
                   @focus="onThresholdFocus(props.option)"
                   @blur="onThresholdBlur"
                   @input="onThresholdInput(props.option.key, 'max', $event.target.value)" />
          </label>
        </div>
      </div>
    </template>
    <template #selection="{ values, isOpen }">
      <span class="multiselect__single" v-if="values.length" v-show="!isOpen">
        {{ $t('settings.generic.selected-items') }} {{ values.length }}
      </span>
    </template>
    <template #placeholder>
      <span>
        {{ $t('settings.generic.select-placeholder') }}
      </span>
    </template>
    <template #beforeList v-if="this.optionsList.length > 0">
      <li class="multiselect__element" @click="toggleOptionsSelection">
        <span class="multiselect__option tracking-multiselect-with-checkbox">
          <input type="checkbox" :checked="areAllOptionsChecked()" @focus.prevent>
          <span class="option__title"><b>{{ $t('settings.generic.select-all') }}</b></span>
        </span>
      </li>
    </template>
  </multiselect>
</template>

<script>

import Multiselect from "vue-multiselect";
import {positionAttributes} from "@/assets/js/position-attributes.js";

export default {
  name: "ChoosePositionProperties",
  components: {Multiselect},
  props: {
    modelValue: {
      type: [Array, Object],
      default() { return []; }
    },
    thresholds: {
      type: Object,
      default() { return {}; }
    },
    preselectAll: {
      type: [Boolean, null],
      default: false
    },
  },
  data() {
    return {
      selectedOptions: [],
      localThresholds: {},
      thresholdInteractionTimeoutHandle: null,
      isThresholdInteractionActive: false,
      optionsList: positionAttributes.getAttributes()
    }
  },
  mounted() {
    this.selectedOptions = this.modelValue;
    this.localThresholds = this.cloneThresholds(this.thresholds);
    document.addEventListener('click', this.onDocumentClick, true);

    if (this.preselectAll) {
      this.selectAllOptions();
    }
  },
  unmounted() {
    document.removeEventListener('click', this.onDocumentClick, true);

    if (this.thresholdInteractionTimeoutHandle) {
      clearTimeout(this.thresholdInteractionTimeoutHandle);
    }
  },
  watch: {
    selectedOptions(value) {
      this.onSelectOptions(value);
    },
    modelValue(value) {
      this.selectedOptions = value;
    },
    thresholds(value) {
      this.localThresholds = this.cloneThresholds(value);
    },
  },
  methods: {
    onDocumentClick(event) {
      const multiselect = this.$refs.multiselect;
      if (!multiselect?.isOpen || multiselect.$el?.contains(event.target)) {
        return;
      }

      if (this.thresholdInteractionTimeoutHandle) {
        clearTimeout(this.thresholdInteractionTimeoutHandle);
        this.thresholdInteractionTimeoutHandle = null;
      }

      this.isThresholdInteractionActive = false;
      multiselect.deactivate();
    },
    cloneThresholds(thresholds = {}) {
      return Object.entries(thresholds).reduce((result, [key, value]) => {
        result[key] = {
          min: value?.min ?? '',
          max: value?.max ?? '',
        };

        return result;
      }, {});
    },
    isOptionChecked(key) {
      return this.selectedOptions?.some(x => x.key === key);
    },
    supportsThresholds(option) {
      return option?.showAsMarkArea !== true;
    },
    areAllOptionsChecked() {
      return this.selectedOptions?.length === this.optionsList.length;
    },
    toggleOptionsSelection() {
      if (this.optionsList.length <= 0) {
        return;
      }

      this.onSelectOptions(this.areAllOptionsChecked() ? [] : this.optionsList);
    },
    selectAllOptions() {
      this.onSelectOptions(this.optionsList);
    },
    onThresholdMouseDown(option) {
      this.activateThresholdInteraction();
      this.ensureOptionSelected(option);
    },
    onThresholdFocus(option) {
      this.activateThresholdInteraction();
      this.ensureOptionSelected(option);
    },
    onThresholdBlur() {
      if (this.thresholdInteractionTimeoutHandle) {
        clearTimeout(this.thresholdInteractionTimeoutHandle);
      }

      this.thresholdInteractionTimeoutHandle = setTimeout(() => {
        this.isThresholdInteractionActive = false;
      }, 0);
    },
    activateThresholdInteraction() {
      if (this.thresholdInteractionTimeoutHandle) {
        clearTimeout(this.thresholdInteractionTimeoutHandle);
      }

      this.isThresholdInteractionActive = true;
    },
    onMultiselectClose() {
      if (!this.isThresholdInteractionActive) {
        return;
      }

      this.$nextTick(() => this.$refs.multiselect?.activate());
    },
    ensureOptionSelected(option) {
      if (this.isOptionChecked(option.key)) {
        return;
      }

      this.onSelectOptions([...this.selectedOptions, option]);
    },
    getThresholdValue(key, field) {
      return this.localThresholds?.[key]?.[field] ?? '';
    },
    onThresholdInput(key, field, value) {
      this.localThresholds = {
        ...this.localThresholds,
        [key]: {
          ...(this.localThresholds[key] ?? {min: '', max: ''}),
          [field]: value === '' ? null : value,
        }
      };

      this.$emit('update:thresholds', this.localThresholds);
    },
    onSelectOptions(data, triggerValueUpdate = true) {
      this.selectedOptions = data;
      if (triggerValueUpdate) {
        this.$emit('update:modelValue', this.selectedOptions);
      }
    },
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/scss/variables.scss';

.attributes-select {
  width: 460px;
  height: 37px;
}

.position-attribute-option {
  width: 100%;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
}

.position-attribute-option-main {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 180px;
}

.position-attribute-thresholds {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.position-attribute-threshold-field {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: 0;
  font-size: 0.8rem;
}

.position-attribute-threshold-input {
  width: 72px;
  min-height: 28px;
}

// Mobile:
@media (max-width: $breaking-point) {
  .attributes-select {
    width: 100%;
  }

  .position-attribute-option {
    align-items: flex-start;
    flex-direction: column;
  }

  .position-attribute-thresholds {
    width: 100%;
    margin-left: 0;
  }

  .position-attribute-threshold-field {
    width: 100%;
    justify-content: space-between;
  }
}
</style>
